import { CircleCheck, CircleX, Info, TriangleAlert, X } from "lucide-react";
import type { FunctionComponent, ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "./cn";
import { FOCUS_RING } from "./focus";

export type AlertType = "success" | "info" | "warning" | "error";

const TYPE_CLASSES: Record<AlertType, { root: string; icon: string }> = {
  success: { root: "bg-noa-success-subtle border-noa-success", icon: "text-noa-success" },
  info: { root: "bg-noa-info-subtle border-noa-info", icon: "text-noa-info" },
  warning: { root: "bg-noa-warning-subtle border-noa-warning", icon: "text-noa-warning" },
  error: { root: "bg-noa-danger-subtle border-noa-danger", icon: "text-noa-danger" },
};

const TYPE_ICONS: Record<AlertType, typeof Info> = {
  success: CircleCheck,
  info: Info,
  warning: TriangleAlert,
  error: CircleX,
};

export interface AlertProps {
  /** Default `info`, or `warning` for a banner (as antd). */
  type?: AlertType;
  /** Headline (antd v5 `message` / v6 `title`). */
  message?: ReactNode;
  description?: ReactNode;
  /** Default false, or true for a banner. */
  showIcon?: boolean;
  /** Replace the default status icon. */
  icon?: ReactNode;
  /** X button that hides the alert and calls `onClose`. */
  closable?: boolean;
  onClose?: () => void;
  /** Content on the right (e.g. a button). */
  action?: ReactNode;
  /** Full-width strip: no border or radius. */
  banner?: boolean;
  className?: string;
  "data-testid"?: string;
}

/** Inline status message (antd `Alert` replacement), tinted with the `-soft` status tokens. */
export const Alert: FunctionComponent<AlertProps> = ({
  type,
  message,
  description,
  showIcon,
  icon,
  closable,
  onClose,
  action,
  banner,
  className,
  "data-testid": testId,
}) => {
  const { t } = useTranslation();
  const [closed, setClosed] = useState(false);

  if (closed) return null;

  const resolvedType = type ?? (banner ? "warning" : "info");
  const withIcon = showIcon ?? banner;
  const withDescription = Boolean(description);
  const Icon = TYPE_ICONS[resolvedType];

  const handleClose = () => {
    setClosed(true);
    onClose?.();
  };

  return (
    <div
      // Only errors/warnings interrupt a screen reader; info/success are polite status messages.
      role={resolvedType === "error" || resolvedType === "warning" ? "alert" : "status"}
      data-testid={testId}
      className={cn(
        "flex text-sm leading-[1.5715] text-noa",
        TYPE_CLASSES[resolvedType].root,
        banner ? "rounded-none border-0" : "rounded-lg border",
        withDescription ? "items-start px-6 py-5" : "items-center px-3 py-2",
        className
      )}
    >
      {withIcon && (
        <span
          className={cn(
            "inline-flex shrink-0",
            TYPE_CLASSES[resolvedType].icon,
            withDescription ? "me-3 [&_svg]:size-6" : "me-2 [&_svg]:size-3.5"
          )}
        >
          {icon ?? <Icon />}
        </span>
      )}
      <div className="min-w-0 flex-1">
        {message && <div className={cn(withDescription && "mb-1 text-base text-noa-strong")}>{message}</div>}
        {description && <div>{description}</div>}
      </div>
      {action && <div className="ms-2 shrink-0">{action}</div>}
      {closable && (
        <button
          type="button"
          aria-label={t("common:close")}
          onClick={handleClose}
          className={cn(
            FOCUS_RING,
            "ms-2 inline-flex shrink-0 cursor-pointer items-center rounded-sm border-0 bg-transparent p-0 text-noa-muted transition-colors hover:text-noa"
          )}
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );
};
